import { AssetProvenanceNotice } from '@/components/multimodal/AssetProvenanceNotice'
import { PagePreview } from '@/components/multimodal/PagePreview'
import { CreatorReview } from './CreatorReview'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CircleDollarSign, ExternalLink, FileText, ImageIcon, Loader2, Mic2, PanelsTopLeft, RotateCw, UsersRound, Video } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { CharacterDesignWorkspace } from '@/components/project/CharacterDesignWorkspace'
import { chapterApi } from '@/api/chapterApi'
import { creditsApi, type CreditEstimate } from '@/api/creditsApi'
import { ApiError, friendlyMessage } from '@/api/http'
import {
  multimodalApi,
  parseScreenplayPayload,
  type AudioVoiceOverride,
  type BGMTrack,
  type ComposeJobView,
  type SketchFigure,
  type SketchFrame,
} from '@/api/multimodalApi'
import { projectApi } from '@/api/projectApi'
import { workbenchApi } from '@/api/workbenchApi'
import { ScreenplayView, StoryboardView } from '@/components/asset/StructuredAssetViews'
import { SketchReviewPanel } from '@/components/multimodal/SketchReviewPanel'
import { PanelPoolGallery, panelPoolCandidatesForPanel, updatePanelPoolPrimary } from '@/components/project/PanelPoolGallery'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { QualityScoreBadge } from '@/components/quality/QualityScoreBadge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { parseAssetQuality } from '@/components/quality/qualityGate'
import { Skeleton } from '@/components/ui/skeleton'
import { creditEstimateLabel, defaultEstimateQuantity, isCreditEstimateInsufficient, requiresVideoCreditConfirmation } from '@/lib/creditEstimate'
import { pageItems } from '@/lib/pagination'
import { STATIONS, type StationId } from '@/lib/pipelineStations'
import { panelImageRenderRequestFromConfig, storyboardConfigFromUnknown } from '@/lib/workbenchConfig'
import type { Asset, AssetLibraryItem, Chapter, ChapterImage, Project } from '@/types/nf'

type PipelineStationId = Exclude<StationId, 'chapter_text'> | 'character_design'

const stationOrder: PipelineStationId[] = [
  ...STATIONS.filter((station) => station.id !== 'chapter_text' && station.id !== 'audio' && station.id !== 'video').map(
    (station) => station.id as PipelineStationId,
  ),
  'character_design',
  'audio',
  'video',
]

const stationIcons: Record<PipelineStationId, typeof FileText> = {
  screenplay: FileText,
  storyboard: PanelsTopLeft,
  panel_image: ImageIcon,
  character_design: UsersRound,
  audio: Mic2,
  video: Video,
}

type PipelineRunStatus = 'pending' | 'running' | 'done' | 'needs_review' | 'failed'
type PipelineRunRequest = { stationId: Exclude<PipelineStationId, 'character_design'>; voiceOverride?: AudioVoiceOverride }
type ComposeAspect = '16:9' | '9:16'
const noBGMValue = '__none__'

const statusColors: Record<PipelineRunStatus, string> = {
  pending: 'var(--gate-review)',
  running: 'var(--gate-running)',
  done: 'var(--gate-done)',
  needs_review: 'var(--gate-review)',
  failed: 'var(--gate-failed)',
}

const PAGE_SIZE = 12

function AssetPagination({
  page,
  pageCount,
  total,
  onPageChange,
}: {
  page: number
  pageCount: number
  total: number
  onPageChange: (page: number) => void
}) {
  const { t } = useTranslation()
  if (pageCount <= 1) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
      <Button type="button" variant="outline" size="sm" disabled={page <= 0} onClick={() => onPageChange(page - 1)}>
        {t('pagination.previous')}
      </Button>
      <span className="text-sm text-muted-foreground">
        {t('pagination.pageWithUnit', { page: page + 1, total: pageCount })}
      </span>
      <span className="text-sm text-muted-foreground">
        {t('pagination.totalImages', { total })}
      </span>
      <Button type="button" variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => onPageChange(page + 1)}>
        {t('pagination.next')}
      </Button>
    </div>
  )
}

function isNotFound(error: unknown) {
  return error instanceof ApiError && error.status === 404
}

function latestAsset(assets: Asset[], assetType: Asset['asset_type']) {
  return assets
    .filter((asset) => asset.asset_type === assetType)
    .sort((a, b) => b.version - a.version || String(b.updated_at).localeCompare(String(a.updated_at)))[0]
}

interface VoiceOption {
  value: string
  label: string
}

interface VoiceProfilePayload {
  kind?: string
  name?: string
  voice?: string
  character?: string
  gender?: string
  age_band?: string
}

function parseVoiceProfileOptions(assets: Asset[]): VoiceOption[] {
  const seen = new Set<string>()
  const options: VoiceOption[] = []
  for (const asset of assets) {
    if (!asset.payload) continue
    let payload: VoiceProfilePayload
    try {
      payload = JSON.parse(asset.payload) as VoiceProfilePayload
    } catch {
      continue
    }
    if (payload.kind !== 'voice_profile') continue
    const value = (payload.voice || payload.name || '').trim()
    if (!value || seen.has(value)) continue
    seen.add(value)
    const labelParts = [payload.name, payload.character].filter(Boolean)
    options.push({ value, label: labelParts.length ? `${labelParts.join(' · ')} (${value})` : value })
  }
  return options
}

function parseVoiceAssetOptions(items: AssetLibraryItem[]): VoiceOption[] {
  const seen = new Set<string>()
  const options: VoiceOption[] = []
  for (const item of items) {
    if (item.type !== 'voice') continue
    const value = String(item.attributes?.voice || item.name || '').trim()
    if (!value || seen.has(value)) continue
    seen.add(value)
    const tags = [voiceGenderLabel(item.attributes?.gender), voiceAgeLabel(item.attributes?.age_band)].filter(Boolean)
    const builtin = item.attributes?.builtin === true ? '内建' : ''
    const suffix = [...tags, builtin].filter(Boolean).join(' · ')
    options.push({ value, label: suffix ? `${item.name} (${suffix})` : item.name })
  }
  return options
}

function uniqueDialogueCharacters(screenplay: ReturnType<typeof parseScreenplayPayload>): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const scene of screenplay?.scenes ?? []) {
    for (const dialogue of scene.dialogues ?? []) {
      const character = dialogue.character?.trim()
      if (!character || seen.has(character)) continue
      seen.add(character)
      out.push(character)
    }
  }
  return out
}

function voiceGenderLabel(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase()
  if (raw === 'm') return '男'
  if (raw === 'f') return '女'
  if (raw === 'neutral') return '中性'
  return raw
}

function voiceAgeLabel(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase()
  const labels: Record<string, string> = {
    child: '儿童',
    teen: '少年',
    youth: '青年',
    middle: '中年',
    senior: '长者',
    elder: '老年',
    any: '通用',
  }
  return labels[raw] ?? raw
}

function voiceValue(selected: string, custom: string) {
  return custom.trim() || (selected === 'config' ? '' : selected.trim())
}

function assetRunStatus(asset?: Asset | null): PipelineRunStatus | null {
  if (!asset) return null
  if (asset.status === 'passed') return 'done'
  if (asset.status === 'needs_review') return 'needs_review'
  if (asset.status === 'failed') return 'failed'
  return 'pending'
}

export function AssetBadge({ asset, status }: { asset?: Asset | null; status?: PipelineRunStatus | null }) {
  const { t } = useTranslation()
  let sourceStale = false
  try { sourceStale = Boolean(JSON.parse(asset?.payload ?? '{}').source_stale) } catch { /* Existing malformed payloads retain the normal status. */ }
  if (sourceStale) return <Badge variant="outline" className="whitespace-normal">{t('chapter.version.sourceStale')}</Badge>
  const displayStatus = status ?? assetRunStatus(asset)

  if (!displayStatus) return <Badge variant="outline">{t('detail.pipelineViewer.notGenerated')}</Badge>
  const label = displayStatus === 'needs_review' ? t('status.asset.needs_review_attention') : t(`detail.pipelineViewer.status.${displayStatus}`)
  return (
    <Badge
      variant="outline"
      className="rounded-md"
      style={{ borderColor: statusColors[displayStatus], color: statusColors[displayStatus] }}
    >
      {label}
    </Badge>
  )
}

function EmptyAsset() {
  const { t } = useTranslation()
  return (
    <div className="rounded-md border border-dashed bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
      {t('detail.pipelineViewer.notGenerated')}
    </div>
  )
}

function StationCard({
  id,
  asset,
  loading,
  error,
  children,
  runStatus,
  regenerating,
  regenerateDisabled,
  regenerateDisabledTitle,
  creditEstimate,
  estimateLoading,
  onBillingClick,
  onOpenWorkbench,
  onRegenerate,
  onContinue,
  continuing,
  chapterId,
  onReviewed,
}: {
  id: PipelineStationId
  asset?: Asset | null
  loading?: boolean
  error?: unknown
  children: React.ReactNode
  runStatus?: PipelineRunStatus | null
  regenerating?: boolean
  regenerateDisabled?: boolean
  regenerateDisabledTitle?: string
  creditEstimate?: CreditEstimate | null
  estimateLoading?: boolean
  onBillingClick?: () => void
  onOpenWorkbench?: () => void
  onRegenerate?: () => void
  onContinue?: () => void
  continuing?: boolean
  chapterId?: string
  onReviewed?: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const Icon = stationIcons[id]
  const insufficientCredits = isCreditEstimateInsufficient(creditEstimate)
  const regenerateTitle = regenerateDisabled && regenerateDisabledTitle
    ? regenerateDisabledTitle
    : t('detail.pipelineViewer.regenerate')
  return (
    <Card className="gap-4 rounded-lg py-4">
      <CardHeader className="gap-2 px-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Icon className="size-4 shrink-0 text-primary" />
            <div className="min-w-0">
              <CardTitle className="truncate text-sm">{t(`detail.pipelineViewer.stations.${id}.title`)}</CardTitle>
              <CardDescription className="mt-1 line-clamp-2">
                {t(`detail.pipelineViewer.stations.${id}.description`)}
              </CardDescription>
            </div>
          </div>
          <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
            {estimateLoading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
            {creditEstimate ? (
              <Badge variant={insufficientCredits ? 'destructive' : 'secondary'} className="rounded-md">
                <CircleDollarSign className="size-3" />
                {creditEstimateLabel(creditEstimate.estimated_credits, i18n.language)}
              </Badge>
            ) : null}
            <QualityScoreBadge quality={parseAssetQuality(asset)} compact />
            {onOpenWorkbench ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8"
                onClick={onOpenWorkbench}
              >
                <ExternalLink className="size-4" />
                {t('detail.pipelineViewer.openWorkbench')}
              </Button>
            ) : null}
            {chapterId && onReviewed && asset?.status === 'needs_review' ? <CreatorReview key={asset.id} chapterId={chapterId} assetId={asset.id} onReviewed={onReviewed} /> : null}
            {onContinue && (asset?.status === 'needs_review' || asset?.cascade_state === 'awaiting_confirm') ? (
              <Button type="button" size="sm" onClick={onContinue} disabled={continuing || loading || asset?.cascade_state === 'running'}>
                {continuing ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" /> : null}
                {t('cascade.actions.continue')}
              </Button>
            ) : null}
            {onRegenerate ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="size-8"
                title={regenerateTitle}
                aria-label={regenerateTitle}
                onClick={onRegenerate}
                disabled={regenerating || regenerateDisabled || insufficientCredits}
              >
                <RotateCw className={regenerating ? 'size-4 animate-spin' : 'size-4'} />
              </Button>
            ) : null}
            {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : <AssetBadge asset={asset} status={runStatus} />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4">
        {error && !isNotFound(error) ? (
          <Alert variant="warning">
            <AlertDescription>
              {friendlyMessage(error, t('detail.pipelineViewer.loadFailed'))}
            </AlertDescription>
          </Alert>
        ) : loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
        ) : insufficientCredits ? (
          <Alert variant="warning" className="mb-3">
            <AlertTriangle className="size-4" />
            <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>{t('detail.pipelineViewer.credits.insufficient')}</span>
              {onBillingClick ? (
                <Button type="button" size="sm" variant="outline" onClick={onBillingClick}>
                  <CircleDollarSign className="size-4" />
                  {t('detail.pipelineViewer.credits.topUp')}
                </Button>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )
}

function ChapterImagesView({ chapterId, images }: { chapterId: string; images: ChapterImage[] }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const doneImages = images.filter((image) => image.status === 'done')
  if (!doneImages.length) return <EmptyAsset />
  const paged = pageItems(doneImages, page, PAGE_SIZE)
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {paged.pageItems.map((image) => (
          <a key={image.id} href={multimodalApi.chapterImageRawUrl(chapterId, image.id)} target="_blank" rel="noreferrer">
            <img
              src={multimodalApi.chapterImageRawUrl(chapterId, image.id)}
              alt={t('detail.pipelineViewer.chapterImageAlt')}
              loading="lazy"
              className="aspect-square w-full rounded-md border bg-muted object-cover"
            />
          </a>
        ))}
      </div>
      <AssetPagination page={paged.page} pageCount={paged.pageCount} total={doneImages.length} onPageChange={setPage} />
    </div>
  )
}

/** simple: 简易档位 —— 聚焦各工位产物与「继续下一工位」, 收起音色/语速细调
 *  与合成画幅等产出后的精修项, 避免挡住第一次跑通流程的用户。
 *  BGM 是例外: 两档都保留下拉以保证曲库供给面可见, 默认仍为无背景音乐。 */
export function ProductionPipelinePanel({
  project,
  chapter,
  simple = false,
  onlyStage,
}: {
  onlyStage?: PipelineStationId
  project: Project
  chapter: Chapter | null
  simple?: boolean
}) {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const chapterId = chapter?.id ?? ''
  const branchId = String(project.current_branch_id ?? 'main')
  const userId = project.user_id
  const [runStatus, setRunStatus] = useState<Partial<Record<PipelineStationId, PipelineRunStatus>>>({})
  const [videoConfirmOpen, setVideoConfirmOpen] = useState(false)
  const [pendingVideoRequest, setPendingVideoRequest] = useState<PipelineRunRequest | null>(null)
  const [narratorVoice, setNarratorVoice] = useState('config')
  const [customNarratorVoice, setCustomNarratorVoice] = useState('')
  const [speechRate, setSpeechRate] = useState('1')
  const [composeAspect, setComposeAspect] = useState<ComposeAspect>('16:9')
  const [composeBGM, setComposeBGM] = useState(noBGMValue)
  const [characterVoices, setCharacterVoices] = useState<Record<string, string>>({})
  const [customCharacterVoices, setCustomCharacterVoices] = useState<Record<string, string>>({})
  const [pendingReplay, setPendingReplay] = useState<{ chapterId: string; assetId: string; knownIds: string[] } | null>(null)

  const assetsQuery = useQuery({
    queryKey: ['chapter-assets', chapterId],
    queryFn: () => chapterApi.assets(chapterId),
    enabled: Boolean(chapterId),
    refetchInterval: pendingReplay?.chapterId === chapterId ? 3000 : false,
  })
  const screenplayQuery = useQuery({
    queryKey: ['chapter-screenplay', chapterId],
    queryFn: () => multimodalApi.getScreenplay(chapterId),
    enabled: Boolean(chapterId),
    retry: false,
  })
  const imagesQuery = useQuery({
    queryKey: ['chapter-images', chapterId],
    queryFn: () => chapterApi.images(chapterId),
    enabled: Boolean(chapterId),
    retry: false,
  })
  const projectAssetsQuery = useQuery({
    queryKey: ['project-assets', project.id],
    queryFn: () => projectApi.assets(project.id),
  })
  const voiceCatalogQuery = useQuery({
    queryKey: ['asset-library', project.id, branchId, 'voice'],
    queryFn: () => projectApi.assetLibrary(project.id, { branchId, type: 'voice' }),
  })
  const bgmQuery = useQuery({
    queryKey: ['av-compose-bgm', project.id],
    queryFn: () => multimodalApi.listBGMTracks(project.id),
  })
  const audioQuery = useQuery({
    queryKey: ['chapter-audio', chapterId],
    queryFn: () => multimodalApi.getAudio(chapterId),
    enabled: Boolean(chapterId),
    retry: false,
  })
  const imageEstimateQuery = useQuery({
    queryKey: ['credits', 'estimate', userId, 'image'],
    queryFn: () => creditsApi.estimate({ service_kind: 'image', quantity: defaultEstimateQuantity('image') }, userId),
    enabled: Boolean(chapterId && userId),
    staleTime: 30_000,
  })
  const audioEstimateQuery = useQuery({
    queryKey: ['credits', 'estimate', userId, 'audio'],
    queryFn: () => creditsApi.estimate({ service_kind: 'audio', quantity: defaultEstimateQuantity('audio') }, userId),
    enabled: Boolean(chapterId && userId),
    staleTime: 30_000,
  })
  const videoEstimateQuery = useQuery({
    queryKey: ['credits', 'estimate', userId, 'video'],
    queryFn: () => creditsApi.estimate({ service_kind: 'video', quantity: defaultEstimateQuantity('video') }, userId),
    enabled: Boolean(chapterId && userId),
    staleTime: 30_000,
    retry: false,
  })
  const videoStationStatusQuery = useQuery({
    queryKey: ['video-station-status', chapterId],
    queryFn: () => multimodalApi.getVideoStationStatus(chapterId),
    enabled: Boolean(chapterId),
    retry: false,
  })
  const videoStationDisabled = videoStationStatusQuery.data?.station_disabled !== false

  const assets = assetsQuery.data ?? []
  useEffect(() => {
    if (!pendingReplay) return
    if (pendingReplay.chapterId !== chapterId) { setPendingReplay(null); return }
    const completed = assetsQuery.data?.some(asset => {
      if (pendingReplay.knownIds.includes(asset.id)) return false
      try { return JSON.parse(asset.payload).replay_of === pendingReplay.assetId } catch { return false }
    })
    if (completed) setPendingReplay(null)
  }, [assetsQuery.data, chapterId, pendingReplay])
  useEffect(() => {
    if (!pendingReplay) return
    const timer = window.setTimeout(() => {
      setPendingReplay(null)
      toast.info(t('detail.pipelineViewer.poolGallery.replayWaiting'))
    }, 240_000)
    return () => window.clearTimeout(timer)
  }, [pendingReplay, t])
  const storyboardAsset = useMemo(() => latestAsset(assets, 'storyboard'), [assets])
  const videoAsset = useMemo(() => latestAsset(assets, 'video'), [assets])
  const characterDesignAsset = useMemo(() => latestAsset(projectAssetsQuery.data ?? [], 'character_design'), [projectAssetsQuery.data])
  const composeMissing = [
    ...(storyboardAsset?.status === 'passed' ? [] : ['storyboard']),
    ...(latestAsset(assets, 'audio') ? [] : ['audio']),
  ]
  const videoUnavailable = videoStationDisabled || assetsQuery.isLoading || Boolean(assetsQuery.error) || composeMissing.length > 0
  const screenplay = parseScreenplayPayload(screenplayQuery.data)

  const panelsQuery = useQuery({
    queryKey: ['storyboard-panels', storyboardAsset?.id],
    queryFn: () => multimodalApi.listStoryboardPanels(storyboardAsset!.id),
    enabled: Boolean(storyboardAsset?.id),
    retry: false,
  })
  const storyboardConfigQuery = useQuery({
    queryKey: ['resolved-workbench-config', project.id, 'storyboard', chapterId],
    queryFn: () => workbenchApi.getResolvedConfig(project.id, 'storyboard', chapterId),
    enabled: Boolean(chapterId),
    retry: false,
  })
  const sketchReviewEnabled = storyboardConfigFromUnknown(storyboardConfigQuery.data?.config_json).sketchReviewEnabled
  const sketchQuery = useQuery({
    queryKey: ['storyboard-sketch', storyboardAsset?.id],
    queryFn: () => multimodalApi.getStoryboardSketch(storyboardAsset!.id),
    enabled: Boolean(storyboardAsset?.id && sketchReviewEnabled),
    retry: false,
  })

  const panels = panelsQuery.data ?? []

  const stationAssets = useMemo(
    () => ({
      screenplay: screenplayQuery.data,
      storyboard: storyboardAsset,
      panel_image: latestAsset(assets, 'panel_image'),
      character_design: characterDesignAsset,
      audio: audioQuery.data,
      video: videoAsset,
    }),
    [assets, audioQuery.data, characterDesignAsset, screenplayQuery.data, storyboardAsset, videoAsset],
  )
  const voiceOptions = useMemo(() => {
    const fromCatalog = parseVoiceAssetOptions(voiceCatalogQuery.data ?? [])
    return fromCatalog.length ? fromCatalog : parseVoiceProfileOptions(projectAssetsQuery.data ?? [])
  }, [projectAssetsQuery.data, voiceCatalogQuery.data])
  const dialogueCharacters = useMemo(() => uniqueDialogueCharacters(screenplay), [screenplay])

  useEffect(() => {
    setRunStatus((current) => {
      let changed = false
      const next = { ...current }
      for (const stationId of stationOrder) {
        if (current[stationId] !== 'pending') continue
        const asset = stationAssets[stationId]
        if (asset?.status === 'passed') {
          next[stationId] = 'done'
          changed = true
        } else if (asset?.status === 'needs_review') {
          next[stationId] = 'needs_review'
          changed = true
        } else if (asset?.status === 'failed') {
          next[stationId] = 'failed'
          changed = true
        }
      }
      return changed ? next : current
    })
  }, [stationAssets])

  const refreshPipeline = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['chapter-assets', chapterId] }),
      queryClient.invalidateQueries({ queryKey: ['chapter-screenplay', chapterId] }),
      queryClient.invalidateQueries({ queryKey: ['chapter-images', chapterId] }),
      queryClient.invalidateQueries({ queryKey: ['chapter-audio', chapterId] }),
      queryClient.invalidateQueries({ queryKey: ['project-assets', project.id] }),
      queryClient.invalidateQueries({ queryKey: ['asset-library', project.id, branchId, 'voice'] }),
      storyboardAsset?.id
        ? queryClient.invalidateQueries({ queryKey: ['storyboard-panels', storyboardAsset.id] })
        : Promise.resolve(),
      storyboardAsset?.id
        ? queryClient.invalidateQueries({ queryKey: ['storyboard-sketch', storyboardAsset.id] })
        : Promise.resolve(),
    ])
  }

  const scheduleRefresh = () => {
    void refreshPipeline()
    for (const delay of [1500, 4000, 8000, 15000]) {
      window.setTimeout(() => void refreshPipeline(), delay)
    }
  }

  const buildAudioVoiceOverride = (): AudioVoiceOverride | undefined => {
    const narrator = voiceValue(narratorVoice, customNarratorVoice)
    const voiceMap = Object.fromEntries(
      dialogueCharacters
        .map((character) => [character, voiceValue(characterVoices[character] ?? 'config', customCharacterVoices[character] ?? '')] as const)
        .filter(([, voice]) => voice),
    )
    const parsedSpeechRate = Number(speechRate)
    const override: AudioVoiceOverride = {}
    if (narrator) override.narrator_voice = narrator
    if (Object.keys(voiceMap).length > 0) override.voice_map = voiceMap
    if (Number.isFinite(parsedSpeechRate) && parsedSpeechRate > 0 && parsedSpeechRate !== 1) override.speech_rate = parsedSpeechRate
    return Object.keys(override).length > 0 ? override : undefined
  }

  const regenerateMutation = useMutation({
    mutationFn: async (request: PipelineRunRequest) => {
      const stationId = request.stationId
      if (!chapterId) throw new Error(t('detail.pipelineViewer.selectChapter'))
      setRunStatus((current) => ({ ...current, [stationId]: 'running' }))
      if (stationId === 'screenplay') return multimodalApi.regenerateScreenplay(chapterId)
      if (stationId === 'storyboard') return multimodalApi.regenerateStoryboard(chapterId)
      if (stationId === 'panel_image') {
        const panelId = panels[0]?.id
        if (!panelId) return multimodalApi.regenerateStoryboard(chapterId)
        const resolved = await workbenchApi.getResolvedConfig(project.id, 'panel_image', chapterId)
        return multimodalApi.renderPanel(panelId, panelImageRenderRequestFromConfig(resolved.config_json))
      }
      if (stationId === 'audio') return multimodalApi.regenerateAudio(chapterId, request.voiceOverride)
      return multimodalApi.regenerateVideo(chapterId)
    },
    onSuccess: async (_data, request) => {
      const stationId = request.stationId
      toast.success(t('detail.pipelineViewer.queued'))
      setRunStatus((current) => ({ ...current, [stationId]: 'pending' }))
      scheduleRefresh()
    },
    onError: (error, request) => {
      const stationId = request.stationId
      setRunStatus((current) => ({ ...current, [stationId]: 'failed' }))
      toast.error(friendlyMessage(error, t('detail.pipelineViewer.regenerateFailed')))
    },
  })

  const sketchUpdateMutation = useMutation({
    mutationFn: ({ panelId, figure }: { panelId: string; figure: SketchFigure }) => {
      if (!storyboardAsset?.id) throw new Error(t('detail.pipelineViewer.notGenerated'))
      return multimodalApi.updateStoryboardSketch(storyboardAsset.id, panelId, { figures: [figure] })
    },
    onSuccess: async () => {
      await Promise.all([
        storyboardAsset?.id ? queryClient.invalidateQueries({ queryKey: ['storyboard-sketch', storyboardAsset.id] }) : Promise.resolve(),
        storyboardAsset?.id ? queryClient.invalidateQueries({ queryKey: ['storyboard-panels', storyboardAsset.id] }) : Promise.resolve(),
      ])
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.pipelineViewer.sketchReview.updateFailed')))
    },
  })
  const sketchConfirmMutation = useMutation({
    mutationFn: () => {
      if (!storyboardAsset?.id) throw new Error(t('detail.pipelineViewer.notGenerated'))
      return multimodalApi.confirmStoryboardSketch(storyboardAsset.id)
    },
    onSuccess: async () => {
      if (storyboardAsset?.id) await queryClient.invalidateQueries({ queryKey: ['storyboard-sketch', storyboardAsset.id] })
    },
    onError: (error) => {
      const message = sketchConfirmErrorMessage(error, t('detail.pipelineViewer.sketchReview.confirmFailed'))
      if (storyboardAsset?.id) {
        const rejected = sketchRejectedPayload(error)
        if (rejected?.panel_id && rejected.vlm_result) {
          queryClient.setQueryData<SketchFrame[]>(['storyboard-sketch', storyboardAsset.id], (current) =>
            (current ?? []).map((frame) => frame.panel_id === rejected.panel_id ? { ...frame, vlm_result: rejected.vlm_result } : frame),
          )
        }
      }
      toast.error(message)
    },
  })
  const sketchPanelRenderMutation = useMutation({
    mutationFn: async (panelId: string) => {
      if (!storyboardAsset?.id) throw new Error(t('detail.pipelineViewer.notGenerated'))
      return multimodalApi.renderStoryboardSketch(storyboardAsset.id, panelId)
    },
    onSuccess: async () => {
      toast.success(t('detail.pipelineViewer.sketchReview.rendered'))
      if (storyboardAsset?.id) await queryClient.invalidateQueries({ queryKey: ['storyboard-sketch', storyboardAsset.id] })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.pipelineViewer.regenerateFailed')))
    },
  })

  const poolReplayMutation = useMutation({
    mutationFn: ({ panelId, assetId }: { panelId: string; assetId: string }) => multimodalApi.replayPanelCandidate(panelId, assetId),
    onSuccess: (_data, { assetId }) => {
      setPendingReplay({ chapterId, assetId, knownIds: assets.map(asset => asset.id) })
      toast.success(t('detail.pipelineViewer.queued'))
      void refreshPipeline()
    },
    onError: error => toast.error(friendlyMessage(error, t('detail.pipelineViewer.regenerateFailed'))),
  })

  const poolSelectMutation = useMutation({
    mutationFn: ({ panelId, assetId }: { panelId: string; assetId: string }) => multimodalApi.selectPanelPoolAsset(panelId, assetId),
    onMutate: async ({ panelId, assetId }) => {
      await queryClient.cancelQueries({ queryKey: ['chapter-assets', chapterId] })
      const previousAssets = queryClient.getQueryData<Asset[]>(['chapter-assets', chapterId])
      queryClient.setQueryData<Asset[]>(['chapter-assets', chapterId], (current) =>
        current ? updatePanelPoolPrimary(current, panelId, assetId) : current,
      )
      return { previousAssets }
    },
    onSuccess: async () => {
      toast.success(t('detail.pipelineViewer.poolGallery.selected'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chapter-assets', chapterId] }),
        queryClient.invalidateQueries({ queryKey: ['chapter-images', chapterId] }),
        storyboardAsset?.id ? queryClient.invalidateQueries({ queryKey: ['storyboard-panels', storyboardAsset.id] }) : Promise.resolve(),
        storyboardAsset?.id ? queryClient.invalidateQueries({ queryKey: ['storyboard-sketch', storyboardAsset.id] }) : Promise.resolve(),
      ])
    },
    onError: (error, _variables, context) => {
      if (context?.previousAssets) queryClient.setQueryData(['chapter-assets', chapterId], context.previousAssets)
      toast.error(friendlyMessage(error, t('detail.pipelineViewer.poolGallery.selectFailed')))
    },
  })

  const continueCascadeMutation = useMutation({
    mutationFn: () => chapterApi.continueCascade(chapterId),
    onSuccess: async (result) => {
      if (result.enqueued) toast.success(t('cascade.toast.continued'))
      else toast.info(t(`cascade.reason.${result.reason || 'not_enqueued'}`, { defaultValue: t('cascade.reason.other', {reason: result.reason || ''}) }))
      await refreshPipeline()
    },
    onError: (error) => toast.error(friendlyMessage(error, t('common.serviceError'))),
  })
  const cascadeControls = {
    chapterId,
    onReviewed: refreshPipeline,
    onContinue: () => continueCascadeMutation.mutate(),
    continuing: continueCascadeMutation.isPending,
  }

  const composeMutation = useMutation({
    mutationFn: async () => {
      if (!chapterId) throw new Error(t('detail.pipelineViewer.selectChapter'))
      setRunStatus((current) => ({ ...current, video: 'running' }))
      const queued = await multimodalApi.composeVideo(chapterId, {
        aspect: composeAspect,
        bgm: composeBGM === noBGMValue ? undefined : composeBGM,
      })
      if (!queued.job_id) return queued
      return waitForComposeJob(chapterId, queued.job_id, refreshPipeline)
    },
    onSuccess: async () => {
      toast.success(t('detail.pipelineViewer.compose.done'))
      setRunStatus((current) => ({ ...current, video: 'done' }))
      await refreshPipeline()
    },
    onError: (error) => {
      setRunStatus((current) => ({ ...current, video: 'failed' }))
      toast.error(friendlyMessage(error, t('detail.pipelineViewer.compose.failed')))
    },
  })
  const stationEstimate = (stationId: PipelineStationId) => {
    if (stationId === 'panel_image' || stationId === 'character_design') return imageEstimateQuery.data
    if (stationId === 'audio') return audioEstimateQuery.data
    if (stationId === 'video') return videoEstimateQuery.data
    return null
  }
  const stationEstimateLoading = (stationId: PipelineStationId) => {
    if (stationId === 'panel_image' || stationId === 'character_design') return imageEstimateQuery.isLoading
    if (stationId === 'audio') return audioEstimateQuery.isLoading
    if (stationId === 'video') return videoEstimateQuery.isLoading
    return false
  }
  const submitRunStation = (request: PipelineRunRequest) => regenerateMutation.mutate(request)
  const runStation = (stationId: Exclude<PipelineStationId, 'character_design'>, voiceOverride?: AudioVoiceOverride) => {
    if (stationId === 'video' && videoStationDisabled) return
    const request = { stationId, voiceOverride }
    const estimate = stationEstimate(stationId)
    if (requiresVideoCreditConfirmation(stationId, estimate)) {
      setPendingVideoRequest(request)
      setVideoConfirmOpen(true)
      return
    }
    submitRunStation(request)
  }
  const activeStation = regenerateMutation.variables?.stationId

  if (!chapter) {
    return (
      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle>{t('detail.pipelineViewer.title')}</CardTitle>
          <CardDescription>{t('detail.pipelineViewer.selectChapter')}</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const openWorkbench = (stationId: Exclude<PipelineStationId, 'character_design'>) => {
    navigate(`/projects/${project.id}/chapters/${chapter.id}/workbench/${stationId}`)
  }

  // The workbench renders a single station; a fixed two-column grid would leave
  // the second column empty, and the page already carries its own heading.
  const visibleStations = stationOrder.filter((stationId) => !onlyStage || stationId === onlyStage)

  return (
    <div className="space-y-4">
      {onlyStage ? null : (
        <div>
          <h2 className="text-lg font-semibold tracking-normal">{t('detail.pipelineViewer.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('detail.pipelineViewer.subtitle', { chapter: chapter.chapter_number, title: chapter.title })}
          </p>
        </div>
      )}
      {!onlyStage ? <PagePreview key={`${project.id}/${chapter.id}`} projectId={project.id} chapterId={chapter.id} /> : null}
      {/* S5 实测：中栏 896px，两列各 423.5px，单工位时右列全空。保留按可见工位数排版。 */}
      <div className={`grid gap-4${visibleStations.length > 1 ? ' xl:grid-cols-2' : ''}`}>
        {visibleStations.map((stationId) => {
          if (stationId === 'screenplay') {
            return (
              <StationCard
                {...cascadeControls}
                key={stationId}
                id={stationId}
                asset={screenplayQuery.data}
                loading={screenplayQuery.isLoading}
                error={screenplayQuery.error}
                runStatus={runStatus[stationId]}
                regenerating={regenerateMutation.isPending && activeStation === stationId}
                creditEstimate={stationEstimate(stationId)}
                estimateLoading={stationEstimateLoading(stationId)}
                onBillingClick={() => navigate('/billing')}
                onOpenWorkbench={() => openWorkbench(stationId)}
                onRegenerate={() => runStation(stationId)}
              >
                <ScreenplayView scenes={screenplay?.scenes ?? []} />
              </StationCard>
            )
          }
          if (stationId === 'storyboard') {
            return (
              <StationCard
                {...cascadeControls}
                key={stationId}
                id={stationId}
                asset={storyboardAsset}
                loading={assetsQuery.isLoading || panelsQuery.isLoading}
                error={assetsQuery.error || panelsQuery.error}
                runStatus={runStatus[stationId]}
                regenerating={regenerateMutation.isPending && activeStation === stationId}
                creditEstimate={stationEstimate(stationId)}
                estimateLoading={stationEstimateLoading(stationId)}
                onBillingClick={() => navigate('/billing')}
                onOpenWorkbench={() => openWorkbench(stationId)}
                onRegenerate={() => runStation(stationId)}
              >
                <div className="space-y-4">
                  {sketchReviewEnabled ? (
                    sketchQuery.isError ? (
                      <Alert variant="destructive">
                        <AlertTriangle className="size-4" />
                        <AlertDescription>{friendlyMessage(sketchQuery.error, t('detail.pipelineViewer.sketchReview.loadFailed'))}</AlertDescription>
                      </Alert>
                    ) : (
                      <SketchReviewPanel
                        frames={sketchQuery.data ?? []}
                        updatingPanelId={sketchUpdateMutation.variables?.panelId ?? null}
                        confirming={sketchConfirmMutation.isPending}
                        rerenderingPanelId={sketchPanelRenderMutation.variables ?? null}
                        selectingSketchCandidate={poolSelectMutation.variables ?? null}
                        onUpdateFigure={(panelId, figure) => sketchUpdateMutation.mutate({ panelId, figure })}
                        onConfirmAll={() => sketchConfirmMutation.mutate()}
                        onRenderPanel={(panelId) => sketchPanelRenderMutation.mutate(panelId)}
                        onSelectSketchCandidate={(panelId, assetId) => poolSelectMutation.mutate({ panelId, assetId })}
                      />
                    )
                  ) : null}
                  <StoryboardView panels={panels} />
                </div>
              </StationCard>
            )
          }
          if (stationId === 'panel_image') {
            return (
              <StationCard
                {...cascadeControls}
                key={stationId}
                id={stationId}
                asset={latestAsset(assets, 'panel_image')}
                loading={imagesQuery.isLoading}
                error={imagesQuery.error}
                runStatus={runStatus[stationId]}
                regenerating={regenerateMutation.isPending && activeStation === stationId}
                creditEstimate={stationEstimate(stationId)}
                estimateLoading={stationEstimateLoading(stationId)}
                onBillingClick={() => navigate('/billing')}
                onOpenWorkbench={() => openWorkbench(stationId)}
                onRegenerate={() => runStation(stationId)}
              >
                <div className="space-y-4">
                  <ChapterImagesView chapterId={chapter.id} images={imagesQuery.data ?? []} />
                  {panels.map((panel) =>
                    panelPoolCandidatesForPanel(panel.id, assets).length > 0 ? (
                      <PanelPoolGallery
                        key={panel.id}
                        panelId={panel.id}
                        assets={assets}
                        selectingAssetId={poolSelectMutation.isPending && poolSelectMutation.variables?.panelId === panel.id ? poolSelectMutation.variables.assetId : null}
                        onSelect={(assetId) => poolSelectMutation.mutate({ panelId: panel.id, assetId })}
                        replayingAssetId={pendingReplay?.assetId ?? (poolReplayMutation.isPending ? poolReplayMutation.variables?.assetId : null)}
                        onReplay={(assetId) => poolReplayMutation.mutate({ panelId: panel.id, assetId })}
                      />
                    ) : null,
                  )}
                </div>
              </StationCard>
            )
          }
          if (stationId === 'character_design') {
            return (
              <StationCard
                key={stationId}
                id={stationId}
                asset={characterDesignAsset}
                loading={projectAssetsQuery.isLoading}
                error={projectAssetsQuery.error}
                runStatus={runStatus[stationId]}
                creditEstimate={stationEstimate(stationId)}
                estimateLoading={stationEstimateLoading(stationId)}
                onBillingClick={() => navigate('/billing')}
              >
                <CharacterDesignWorkspace projectId={project.id} branchId={branchId} />
              </StationCard>
            )
          }
          if (stationId === 'audio') {
            return (
              <StationCard
                {...cascadeControls}
                key={stationId}
                id={stationId}
                asset={audioQuery.data}
                loading={audioQuery.isLoading}
                error={audioQuery.error}
                runStatus={runStatus[stationId]}
                regenerating={regenerateMutation.isPending && activeStation === stationId}
                creditEstimate={stationEstimate(stationId)}
                estimateLoading={stationEstimateLoading(stationId)}
                onBillingClick={() => navigate('/billing')}
                onOpenWorkbench={() => openWorkbench(stationId)}
                onRegenerate={() => runStation(stationId, buildAudioVoiceOverride())}
              >
                {simple ? null : (
                  <div className="space-y-3">
                    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_8rem]">
                      <div className="space-y-1.5">
                        <Label>{t('detail.pipelineViewer.audioWorkbench.narratorVoice')}</Label>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Select value={narratorVoice} onValueChange={setNarratorVoice}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="config">{t('detail.pipelineViewer.audioWorkbench.configDefault')}</SelectItem>
                              {voiceOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={customNarratorVoice}
                            onChange={(event) => setCustomNarratorVoice(event.target.value)}
                            placeholder={t('detail.pipelineViewer.audioWorkbench.customVoicePlaceholder')}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="audio-speech-rate">{t('detail.pipelineViewer.audioWorkbench.speechRate')}</Label>
                        <Input
                          id="audio-speech-rate"
                          type="number"
                          min="0.5"
                          max="2"
                          step="0.05"
                          value={speechRate}
                          onChange={(event) => setSpeechRate(event.target.value)}
                        />
                      </div>
                    </div>
                    {dialogueCharacters.length ? (
                      <div className="space-y-2">
                        <Label>{t('detail.pipelineViewer.audioWorkbench.characterVoices')}</Label>
                        <div className="grid gap-2">
                          {dialogueCharacters.slice(0, 6).map((character) => (
                            <div key={character} className="grid gap-2 sm:grid-cols-[minmax(6rem,10rem)_minmax(0,1fr)_minmax(0,1fr)] sm:items-center">
                              <span className="truncate text-sm text-muted-foreground">{character}</span>
                              <Select
                                value={characterVoices[character] ?? 'config'}
                                onValueChange={(value) => setCharacterVoices((current) => ({ ...current, [character]: value }))}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="config">{t('detail.pipelineViewer.audioWorkbench.configDefault')}</SelectItem>
                                  {voiceOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                value={customCharacterVoices[character] ?? ''}
                                onChange={(event) =>
                                  setCustomCharacterVoices((current) => ({ ...current, [character]: event.target.value }))
                                }
                                placeholder={t('detail.pipelineViewer.audioWorkbench.customVoicePlaceholder')}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {audioQuery.data ? (
                      <audio controls preload="metadata" src={multimodalApi.audioRawUrl(chapter.id)} className="w-full">
                        {t('detail.pipelineViewer.mediaUnsupported')}
                      </audio>
                    ) : (
                      <EmptyAsset />
                    )}
                  </div>
                )}
              </StationCard>
            )
          }
          return (
            <StationCard
                {...cascadeControls}
              key={stationId}
              id={stationId}
              asset={videoAsset}
              loading={assetsQuery.isLoading}
              error={assetsQuery.error}
              runStatus={runStatus[stationId]}
              regenerating={regenerateMutation.isPending && activeStation === stationId}
              regenerateDisabled={videoUnavailable}
              regenerateDisabledTitle={videoStationDisabled ? t('errors.codes.video_station_disabled') : t('detail.pipelineViewer.compose.missing', { items: composeMissing.map((item) => t(`workbench.stations.${item}`)).join('、') })}
              creditEstimate={stationEstimate(stationId)}
              estimateLoading={stationEstimateLoading(stationId)}
              onBillingClick={() => navigate('/billing')}
              onOpenWorkbench={() => openWorkbench(stationId)}
              onRegenerate={() => runStation(stationId)}
            >
              {videoAsset ? (
                <div className="space-y-3">
                  <AssetProvenanceNotice payload={videoAsset.payload} />
 <video controls preload="metadata" src={multimodalApi.videoRawUrl(chapter.id)} className="max-h-96 w-full rounded-md bg-muted">
                    {t('detail.pipelineViewer.mediaUnsupported')}
                  </video>
                  <VideoComposeControls
                    simple={simple}
                    aspect={composeAspect}
                    bgm={composeBGM}
                    bgmTracks={bgmQuery.data ?? []}
                    pending={composeMutation.isPending}
                    disabled={videoUnavailable}
                    missing={composeMissing}
                    onAspectChange={setComposeAspect}
                    onBGMChange={setComposeBGM}
                    onCompose={() => composeMutation.mutate()}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <EmptyAsset />
                  <VideoComposeControls
                    simple={simple}
                    aspect={composeAspect}
                    bgm={composeBGM}
                    bgmTracks={bgmQuery.data ?? []}
                    pending={composeMutation.isPending}
                    disabled={videoUnavailable}
                    missing={composeMissing}
                    onAspectChange={setComposeAspect}
                    onBGMChange={setComposeBGM}
                    onCompose={() => composeMutation.mutate()}
                  />
                </div>
              )}
            </StationCard>
          )
        })}
      </div>
      <Dialog open={videoConfirmOpen} onOpenChange={setVideoConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.pipelineViewer.credits.videoConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('detail.pipelineViewer.credits.videoConfirmDescription', {
                credits: creditEstimateLabel(videoEstimateQuery.data?.estimated_credits, i18n.language),
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter showCloseButton>
            <Button
              type="button"
              variant="destructive"
              title={videoStationDisabled ? t('errors.codes.video_station_disabled') : undefined}
              disabled={
                videoStationDisabled ||
                !pendingVideoRequest ||
                regenerateMutation.isPending ||
                isCreditEstimateInsufficient(videoEstimateQuery.data)
              }
              onClick={() => {
                if (!pendingVideoRequest) return
                setVideoConfirmOpen(false)
                submitRunStation(pendingVideoRequest)
                setPendingVideoRequest(null)
              }}
            >
              {regenerateMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Video className="size-4" />}
              {t('detail.pipelineViewer.credits.videoConfirmSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function VideoComposeControls({
  simple = false,
  aspect,
  bgm,
  bgmTracks,
  pending,
  disabled,
  missing,
  onAspectChange,
  onBGMChange,
  onCompose,
}: {
  simple?: boolean
  aspect: ComposeAspect
  bgm: string
  bgmTracks: BGMTrack[]
  pending: boolean
  disabled: boolean
  missing: string[]
  onAspectChange: (aspect: ComposeAspect) => void
  onBGMChange: (bgm: string) => void
  onCompose: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
      {missing.length > 0 ? <p role="status" className="text-sm text-muted-foreground">{t('detail.pipelineViewer.compose.missing', { items: missing.map((item) => t(`workbench.stations.${item}`)).join('、') })}</p> : null}
      {/* 画幅仅高级档可调; BGM 两档都可选, 默认仍为无背景音乐。 */}
      {simple ? null : (
        <Select value={aspect} onValueChange={(value) => onAspectChange(value as ComposeAspect)}>
          <SelectTrigger className="h-9 w-full sm:w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="16:9">{t('detail.pipelineViewer.compose.aspectLandscape')}</SelectItem>
            <SelectItem value="9:16">{t('detail.pipelineViewer.compose.aspectPortrait')}</SelectItem>
          </SelectContent>
        </Select>
      )}
      <Select value={bgm} onValueChange={onBGMChange}>
        <SelectTrigger className="h-9 w-full sm:w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={noBGMValue}>{t('detail.pipelineViewer.compose.bgmNone')}</SelectItem>
          {bgmTracks.map((track) => (
            <SelectItem key={track.name} value={track.name}>
              {track.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" variant="secondary" className="h-9" disabled={pending || disabled} onClick={onCompose}>
        {pending ? <Loader2 className="size-4 animate-spin" /> : <Video className="size-4" />}
        {t('detail.pipelineViewer.compose.submit')}
      </Button>
    </div>
  )
}

async function waitForComposeJob(chapterId: string, jobId: string, refresh: () => Promise<unknown> | unknown): Promise<ComposeJobView> {
  for (let attempt = 0; attempt < 80; attempt += 1) {
    const view = await multimodalApi.getComposeJob(chapterId, jobId)
    await refresh()
    if (view.status === 'done') return view
    if (view.status === 'failed') throw new Error(view.error || 'compose_failed')
    await new Promise((resolve) => window.setTimeout(resolve, 1500))
  }
  throw new Error('compose_poll_timeout')
}

function sketchRejectedPayload(error: unknown): { panel_id?: string; score?: number; issues?: Array<{ message?: string; code?: string }>; vlm_result?: SketchFrame['vlm_result'] } | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== 'object') return null
  const data = error.data as { panel_id?: unknown; score?: unknown; issues?: unknown; vlm_result?: unknown }
  return {
    panel_id: typeof data.panel_id === 'string' ? data.panel_id : undefined,
    score: typeof data.score === 'number' ? data.score : undefined,
    issues: Array.isArray(data.issues) ? data.issues as Array<{ message?: string; code?: string }> : undefined,
    vlm_result: data.vlm_result as SketchFrame['vlm_result'],
  }
}

function sketchConfirmErrorMessage(error: unknown, fallback: string): string {
  const rejected = sketchRejectedPayload(error)
  if (!rejected) return friendlyMessage(error, fallback)
  const issue = rejected.issues?.find((item) => item.message || item.code)
  const suffix = issue?.message || issue?.code
  const score = typeof rejected.score === 'number' ? `VLM ${rejected.score}` : ''
  return [fallback, score, suffix].filter(Boolean).join(' · ')
}
